import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/core/string.core';
import { Loader } from '../loader/loader.component';

const buttonVariants = cva(
  cn(
    'typo-primary-500 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded focus-visible:outline-none px-3 py-2 h-10',
  ),
  {
    variants: {
      variant: {
        default: cn(
          'bg-primary hover:bg-primary-hover text-white-text disabled:pointer-events-none',
          'disabled:bg-light-gray disabled:text-disabled-text disabled:border-none',
          'focus-visible:border-primary focus-visible:outline-button-focus focus-visible:outline-offset-0',
        ),
        secondary: cn(
          'bg-white border border-other-stroke',
          'hover:border-primary',
          'active:border-primary',
          'focus-visible:border-primary focus-visible:outline-button-focus focus-visible:outline-offset-0',
          'disabled:bg-light-gray disabled:text-disabled-text disabled:border-none',
        ),
        ghost: cn(
          'inline-flex justify-start',
          'p-0 h-auto hover:underline border border-transparent',
          'focus-visible:border-primary focus-visible:outline-button-focus focus-visible:outline-offset-0',
          'h-auto min-h-auto max-h-auto',
        ),
        pill: cn(
          cn(
            'inline-flex justify-start',
            'p-0 h-auto border border-transparent',
            'focus-visible:border-primary focus-visible:outline-button-focus focus-visible:outline-offset-0',
            'h-auto min-h-auto max-h-auto',
          ),
        ),
        icon: cn(
          'p-0 group h-auto border border-transparent',
          'focus-visible:border-primary focus-visible:outline-button-focus focus-visible:outline-offset-1',
        ),
        topbar: 'p-0 h-auto group',
        'navbar-icon': 'p-0 hover:bg-navbar-icon-hover h-8 w-8 group focus-visible:outline-none',
        'navbar-user': 'p-0 hover:bg-navbar-icon-hover h-8 group focus-visible:outline-none',
      },
      loading: {
        true: 'bg-light-gray pointer-events-none cursor-not-allowed text-secondary-text disabled:pointer-events-none disabled:bg-light-gray disabled:text-secondary-text',
        false: '',
      },
      size: {
        default: 'min-h-10 h-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  index?: number;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, children, variant, size, loading, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        disabled={props.disabled}
        className={cn(
          buttonVariants({
            variant,
            size,
            loading,
            className,
          }),
        )}
        ref={ref}
        {...props}
      >
        {loading && <Loader className="w-5 h-5 [&>span]:border-y-secondary-text [&>span]:border-x-transparent" />}
        {!loading && children}
      </Comp>
    );
  },
);

Button.displayName = 'Button';

export default Button;
